import React from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faFilter, faSpinner, faRotateLeft, faMagnifyingGlass, faChevronDown } from '@fortawesome/free-solid-svg-icons'
import { auditService, type AuditLogQuery, type AuditLogRow } from '../services/auditService'
import { apiKeysService } from '../services/apiKeysService'
import FormCompletionsAuditPanel from './FormCompletionsAuditPanel'
import SuggestInput, { resolveSuggestValue, type SuggestOption } from './SuggestInput'
import type { User } from '../services/userService'

type Props = {
  /** When false, skips loading. Defaults to true when mounted. */
  active?: boolean
  users?: User[]
  namesByEmpId?: Record<string, string>
}

type AuditView = 'executions' | 'completions'

function employeeSuggestOptions(
  users: User[],
  namesByEmpId: Record<string, string>,
): SuggestOption[] {
  return users
    .filter((user) => String(user.employeeId || '').trim())
    .map((user) => {
      const employeeId = String(user.employeeId)
      const name = namesByEmpId[employeeId] || ''
      const username = user.username || ''
      return {
        value: employeeId,
        label: name ? `${employeeId} · ${name}` : employeeId,
        detail: [username, user.accessLevel].filter(Boolean).join(' · ') || undefined,
        keywords: [name, username, user.accessLevel].filter(Boolean).join(' '),
      }
    })
}

/** Gateway `app` values, plus HR (stored as profile + hr.* / eeap.*). */
const APPS: { id: string; label: string }[] = [
  { id: 'admin', label: 'Admin' },
  { id: 'controlCenter', label: 'Control Center' },
  { id: 'forms', label: 'Forms' },
  { id: 'gis', label: 'GIS' },
  { id: 'global', label: 'Global' },
  { id: 'hr', label: 'HR' },
  { id: 'pr', label: 'PR' },
  { id: 'profile', label: 'Profile' },
  { id: 'projects', label: 'Projects' },
  { id: 'resources', label: 'Resources' },
  { id: 'tally', label: 'Tally' },
]

function isHrCapability(capability: string) {
  return /^(hr|eeap)\./.test(String(capability || ''))
}

function auditAppLabel(row: AuditLogRow) {
  if (row.app === 'profile') return isHrCapability(row.capability) ? 'HR' : 'Profile'
  return APPS.find((a) => a.id === row.app)?.label || row.app
}

function subjectEmployeeId(row: AuditLogRow) {
  const raw = row.inputRedacted?.employeeId
  return raw == null ? '' : String(raw).trim()
}

function AuditPersonCell({
  employeeId,
  namesByEmpId,
  detail,
}: {
  employeeId?: string | null
  namesByEmpId: Record<string, string>
  detail?: string
}) {
  const id = String(employeeId || '').trim()
  if (!id) return <span className="text-base-content/60">—</span>
  const name = namesByEmpId[id] || ''
  return (
    <>
      <div>{name || id}</div>
      {name && name !== id ? <div className="text-xs text-base-content/50">{id}</div> : null}
      {detail ? <div className="text-xs text-base-content/50">{detail}</div> : null}
    </>
  )
}

function toDateTimeLocalValue(iso: string | null): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const pad = (n: number) => String(n).padStart(2, '0')
  const yyyy = d.getFullYear()
  const mm = pad(d.getMonth() + 1)
  const dd = pad(d.getDate())
  const hh = pad(d.getHours())
  const mi = pad(d.getMinutes())
  return `${yyyy}-${mm}-${dd}T${hh}:${mi}`
}

function fromDateTimeLocalValue(v: string): string | undefined {
  if (!v) return undefined
  const d = new Date(v)
  if (Number.isNaN(d.getTime())) return undefined
  return d.toISOString()
}

function fmtTs(isoOrDate: any): string {
  const d = new Date(isoOrDate)
  if (Number.isNaN(d.getTime())) return String(isoOrDate || '')
  return d.toLocaleString()
}

function badgeForStatus(statusCode: number) {
  if (statusCode >= 500) return 'badge-error'
  if (statusCode >= 400) return 'badge-warning'
  if (statusCode >= 300) return 'badge-info'
  return 'badge-success'
}

function statusLabel(statusCode: number) {
  if (statusCode >= 500) return `${statusCode} server`
  if (statusCode >= 400) return `${statusCode} client`
  if (statusCode >= 300) return `${statusCode} redirect`
  return `${statusCode} ok`
}

export default function AuditLogsModal({
  active = true,
  users = [],
  namesByEmpId = {},
}: Props) {
  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [rows, setRows] = React.useState<AuditLogRow[]>([])
  const [expandedId, setExpandedId] = React.useState<string | null>(null)

  const [limit, setLimit] = React.useState(50)
  const [app, setApp] = React.useState<string>('')
  const [capability, setCapability] = React.useState<string>('')
  const [employeeId, setEmployeeId] = React.useState<string>('')
  const [success, setSuccess] = React.useState<'any' | 'true' | 'false'>('any')
  const [capabilityOptions, setCapabilityOptions] = React.useState<SuggestOption[]>([])
  const [fromTs, setFromTs] = React.useState<string>('')
  const [toTs, setToTs] = React.useState<string>('')
  const [initialized, setInitialized] = React.useState(false)
  const [view, setView] = React.useState<AuditView>('executions')

  const employeeOptions = React.useMemo(
    () => employeeSuggestOptions(users, namesByEmpId),
    [users, namesByEmpId],
  )

  const visibleCapabilityOptions = React.useMemo(() => {
    if (app === 'hr') return capabilityOptions.filter((option) => isHrCapability(option.value))
    if (app === 'profile') return capabilityOptions.filter((option) => !isHrCapability(option.value))
    return capabilityOptions
  }, [app, capabilityOptions])

  React.useEffect(() => {
    if (!active) return undefined
    let cancelled = false
    apiKeysService
      .catalog()
      .then((groups) => {
        if (cancelled) return
        setCapabilityOptions(
          groups.flatMap((group) =>
            group.capabilities.map((key) => ({
              value: key,
              label: key,
              detail: group.label,
              keywords: `${group.domain} ${group.label}`,
            })),
          ),
        )
      })
      .catch(() => {
        if (!cancelled) setCapabilityOptions([])
      })
    return () => {
      cancelled = true
    }
  }, [active])

  const buildQuery = (toOverride?: string): AuditLogQuery => {
    const q: AuditLogQuery = { limit }
    if (app) q.app = app
    const resolvedCapability = resolveSuggestValue(capability, visibleCapabilityOptions)
    const resolvedEmployeeId = resolveSuggestValue(employeeId, employeeOptions)
    if (resolvedCapability) q.capability = resolvedCapability
    if (resolvedEmployeeId) q.employeeId = resolvedEmployeeId
    if (success !== 'any') q.success = success === 'true'
    const fromIso = fromDateTimeLocalValue(fromTs)
    const toIso = fromDateTimeLocalValue(toOverride ?? toTs)
    if (fromIso) q.fromTs = fromIso
    if (toIso) q.toTs = toIso
    return q
  }

  const load = async () => {
    setLoading(true)
    setError(null)
    try {
      const nowLocal = toDateTimeLocalValue(new Date().toISOString())
      let toForQuery = toTs
      if (toTs && nowLocal && toTs < nowLocal) {
        const toMs = new Date(toTs).getTime()
        if (Number.isFinite(toMs) && Date.now() - toMs < 15 * 60 * 1000) {
          toForQuery = nowLocal
          setToTs(nowLocal)
        }
      }
      const data = await auditService.listCapabilityExecLogs(buildQuery(toForQuery))
      setRows(data)
    } catch (e: any) {
      setError(e?.message || 'Failed to load audit logs')
      setRows([])
    } finally {
      setLoading(false)
    }
  }

  React.useEffect(() => {
    if (!active) return
    // Default range: last 24h (helps avoid huge first load)
    const now = new Date()
    const from = new Date(now.getTime() - 24 * 60 * 60 * 1000)
    setFromTs(toDateTimeLocalValue(from.toISOString()))
    setToTs(toDateTimeLocalValue(now.toISOString()))
    setLimit(50)
    setApp('')
    setCapability('')
    setEmployeeId('')
    setSuccess('any')
    setRows([])
    setError(null)
    setExpandedId(null)
    setView('executions')
    setInitialized(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active])

  React.useEffect(() => {
    if (!active || !initialized) return
    if (view !== 'executions') return
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, initialized, view])

  if (!active) return null

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-bold">Audit Logs</h2>
        <p className="text-sm opacity-70">
          {view === 'completions'
            ? 'Archived completed form PDFs with actor and client metadata.'
            : 'Capability gateway executions (`/api/capabilities/execute`).'}
        </p>
      </div>

      <div role="tablist" className="tabs tabs-lift w-fit">
        <button
          type="button"
          role="tab"
          className={`tab ${view === 'executions' ? 'tab-active' : ''}`}
          onClick={() => setView('executions')}
        >
          Capability executions
        </button>
        <button
          type="button"
          role="tab"
          className={`tab ${view === 'completions' ? 'tab-active' : ''}`}
          onClick={() => setView('completions')}
        >
          Form completions
        </button>
      </div>

      {view === 'completions' ? (
        <FormCompletionsAuditPanel
          active={active && initialized}
          fromTs={fromTs}
          toTs={toTs}
          limit={limit}
          onFromTsChange={setFromTs}
          onToTsChange={setToTs}
          onLimitChange={setLimit}
          toDateTimeLocalValue={toDateTimeLocalValue}
          fromDateTimeLocalValue={fromDateTimeLocalValue}
          fmtTs={fmtTs}
          employeeOptions={employeeOptions}
        />
      ) : (
      <>
      <div className="rounded-xl border border-base-300 bg-base-200/40 p-4">
        <div className="flex items-center gap-2 mb-3">
          <FontAwesomeIcon icon={faFilter} />
          <span className="font-semibold">Filters</span>
          <span className="text-xs opacity-70 hidden sm:inline">Narrow results before loading lots of logs</span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
          <div className="form-control flex flex-col">
            <label className="label py-1"><span className="label-text">App</span></label>
            <select className="select select-bordered" value={app} onChange={(e) => setApp(e.target.value)}>
              <option value="">All</option>
              {APPS.map((a) => (
                <option key={a.id} value={a.id}>{a.label}</option>
              ))}
            </select>
          </div>

          <div className="form-control xl:col-span-2 flex flex-col">
            <label className="label py-1"><span className="label-text">Capability</span></label>
            <SuggestInput
              value={capability}
              onChange={setCapability}
              options={visibleCapabilityOptions}
              placeholder="Search capabilities…"
              aria-label="Capability"
            />
          </div>

          <div className="form-control flex flex-col">
            <label className="label py-1"><span className="label-text">Success</span></label>
            <select className="select select-bordered" value={success} onChange={(e) => setSuccess(e.target.value as any)}>
              <option value="any">Any</option>
              <option value="true">Success</option>
              <option value="false">Failure</option>
            </select>
          </div>

          <div className="form-control flex flex-col">
            <label className="label py-1"><span className="label-text">Employee</span></label>
            <SuggestInput
              value={employeeId}
              onChange={setEmployeeId}
              options={employeeOptions}
              placeholder="Search ID, name, or username…"
              aria-label="Employee"
            />
            <span className="text-xs text-base-content/50 mt-1">Actor or the employee on an HR transaction.</span>
          </div>

          <div className="form-control flex flex-col">
            <label className="label py-1"><span className="label-text">From</span></label>
            <input className="input input-bordered" type="datetime-local" value={fromTs} onChange={(e) => setFromTs(e.target.value)} />
          </div>

          <div className="form-control flex flex-col">
            <label className="label py-1"><span className="label-text">To</span></label>
            <input className="input input-bordered" type="datetime-local" value={toTs} onChange={(e) => setToTs(e.target.value)} />
          </div>

          <div className="form-control flex flex-col">
            <label className="label py-1"><span className="label-text">Limit</span></label>
            <select className="select select-bordered" value={String(limit)} onChange={(e) => setLimit(Number(e.target.value))}>
              {[25, 50, 100, 200].map((n) => (
                <option key={n} value={String(n)}>{n}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-2 justify-end mt-4">
          <button className="btn btn-primary sm:w-40" onClick={load} disabled={loading}>
            {loading ? <FontAwesomeIcon icon={faSpinner} className="animate-spin" /> : <FontAwesomeIcon icon={faMagnifyingGlass} />}
            <span className="ml-2">Apply</span>
          </button>
          <button
            className="btn btn-outline sm:w-40"
            disabled={loading}
            onClick={() => {
              setApp('')
              setCapability('')
              setEmployeeId('')
              setSuccess('any')
              const now = new Date()
              const from = new Date(now.getTime() - 24 * 60 * 60 * 1000)
              setFromTs(toDateTimeLocalValue(from.toISOString()))
              setToTs(toDateTimeLocalValue(now.toISOString()))
              setLimit(50)
            }}
          >
            <FontAwesomeIcon icon={faRotateLeft} />
            <span className="ml-2">Reset</span>
          </button>
        </div>
      </div>

      {error && (
        <div className="alert alert-error">
          <span>{error}</span>
        </div>
      )}

      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="text-sm opacity-70">{rows.length} result(s)</div>
        </div>

        <div className="overflow-x-auto">
          <table className="table table-sm">
            <thead>
              <tr>
                <th>Time</th>
                <th>User</th>
                <th>Employee</th>
                <th>App</th>
                <th>Capability</th>
                <th>Status</th>
                <th>Duration</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const subjectId = subjectEmployeeId(r)
                return (
                <React.Fragment key={r._id}>
                  <tr>
                    <td className="whitespace-nowrap">{fmtTs(r.ts)}</td>
                    <td>
                      {r.apiKeyId || r.apiKeyPrefix ? (
                        <>
                          <div>{r.apiKeyName || 'API key'}</div>
                          <div className="text-xs text-base-content/50 font-mono">
                            lts_{r.apiKeyPrefix || '…'}_…
                          </div>
                        </>
                      ) : (
                        <AuditPersonCell
                          employeeId={r.employeeId}
                          namesByEmpId={namesByEmpId}
                          detail={r.accessLevel || undefined}
                        />
                      )}
                    </td>
                    <td>
                      <AuditPersonCell employeeId={subjectId} namesByEmpId={namesByEmpId} />
                    </td>
                    <td>{auditAppLabel(r)}</td>
                    <td className="font-mono text-xs">{r.capability}</td>
                    <td>
                      <span className={`badge badge-sm ${badgeForStatus(r.statusCode)}`}>{statusLabel(r.statusCode)}</span>
                    </td>
                    <td className="whitespace-nowrap">{r.durationMs}ms</td>
                    <td>
                      <button
                        type="button"
                        className="btn btn-ghost btn-xs"
                        onClick={() => setExpandedId((cur) => (cur === r._id ? null : r._id))}
                      >
                        Details
                        <FontAwesomeIcon icon={faChevronDown} className={`ml-1 text-xs transition-transform ${expandedId === r._id ? 'rotate-180' : ''}`} />
                      </button>
                    </td>
                  </tr>
                  {expandedId === r._id && (
                    <tr>
                      <td colSpan={8} className="bg-base-200/40">
                        <div className="p-3">
                          <div className="text-xs text-base-content/60 mb-2 flex flex-col gap-1">
                            {r.error?.message ? (
                              <div className="text-error"><span className="font-semibold">error:</span> {r.error.message}</div>
                            ) : null}
                            <div><span className="font-semibold">requestId:</span> <span className="font-mono">{r.requestId || '—'}</span></div>
                            <div><span className="font-semibold">apiKeyId:</span> <span className="font-mono">{r.apiKeyId || '—'}</span></div>
                            <div><span className="font-semibold">ip:</span> <span className="font-mono">{r.ip || '—'}</span></div>
                            <div><span className="font-semibold">keys:</span> {(r.inputKeys || []).join(', ') || '—'}</div>
                            <div><span className="font-semibold">bytesApprox:</span> {typeof r.inputBytesApprox === 'number' ? r.inputBytesApprox : '—'}</div>
                          </div>

                          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                            <div className="bg-base-100 border border-base-300 rounded p-2">
                              <div className="text-xs font-semibold mb-1">input (redacted)</div>
                              <pre className="text-xs whitespace-pre-wrap break-words">{JSON.stringify(r.inputRedacted ?? null, null, 2)}</pre>
                            </div>
                            <div className="bg-base-100 border border-base-300 rounded p-2">
                              <div className="text-xs font-semibold mb-1">error</div>
                              <pre className="text-xs whitespace-pre-wrap break-words">{JSON.stringify(r.error ?? null, null, 2)}</pre>
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
                )
              })}
              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={8} className="text-base-content/60">No logs found for these filters.</td>
                </tr>
              )}
              {loading && (
                <tr>
                  <td colSpan={8} className="text-base-content/60">
                    <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
                    Loading…
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      </>
      )}
    </div>
  )
}
